import React, { lazy } from 'react';
import PropTypes from 'prop-types';
import { Route, Redirect } from 'react-router-dom';

import { addLocale } from '../misc';
import request, { apiHost } from '../utils/request';
import { CONSTANT } from '../utils/constant';
import directAccessConfigs from '../utils/directAccessConfig';

const UpdatePassword = lazy(() => import('../updatePassword'));
const Error401 = lazy(() => import('../error/401'));

const PrivateRoute = ({ locale, component: Component, ...rest }) => {
  const token = localStorage.accessToken;
  const currentUrl = window.location.pathname;
  const addLocaleToUrl = addLocale(locale);
  const isPasswordChangeRequired = request.getPasswordUpdate();
  const {
    KEYS: {
      ROLES: {
        ADMIN_TIME_TRACKING,
        MANAGER_TIME_TRACKING,
        TEAM_MEMBER_TIME_TRACKING
      }
    }
  } = CONSTANT;

  // Generic logic to set redirectAfterLogin if user is not logged in and visits direct URL
  if (!token) {
    const matchedConfig = Object.values(directAccessConfigs).find(config =>
      config.directUrls.includes(currentUrl)
    );

    if (
      matchedConfig &&
      matchedConfig.webformUrls &&
      matchedConfig.webformUrls[locale]
    ) {
      localStorage.setItem(
        'redirectAfterLogin',
        matchedConfig.webformUrls[locale]
      );
      localStorage.setItem('isRedirectValid', true);
    }
  }

  const getRoute = props => {
    // eslint-disable-next-line react/prop-types
    const { pathname } = props.location;

    // Generic redirect for any direct access URL
    const matchedConfig = Object.values(directAccessConfigs).find(config =>
      config.directUrls.includes(pathname)
    );
    const matchedConfigCurrent = Object.values(
      directAccessConfigs
    ).find(config => config.directUrls.includes(currentUrl));

    if (!token && matchedConfigCurrent) {
      localStorage.setItem(
        'redirectAfterLogin',
        matchedConfigCurrent.webformUrls[locale] || '/'
      );
      localStorage.setItem('isRedirectValid', true);
      // Hard redirect - use partner SSO if configured, otherwise use regular SSO
      const ssoEndpoint = matchedConfigCurrent.usePartnerSSO
        ? `${apiHost}/users/sso-login-partners`
        : `${apiHost}/users/sso-login`;
      window.location.replace(ssoEndpoint);
      return null;
    }

    if (token && matchedConfig) {
      const target = matchedConfig.webformUrls[locale] || '/';
      return <Redirect to={target} />;
    }

    if (token) {
      const {
        role: { name }
      } = request.getProfile();

      /** If Role is Manager or Team Member for Time Tracking Tool show 401 unauthorized */
      if ([MANAGER_TIME_TRACKING, TEAM_MEMBER_TIME_TRACKING].includes(name)) {
        return <Error401 />;
      }

      /** If Role is ADMIN for Time Tracking Tool redirect to Manage Account */
      if (
        name === ADMIN_TIME_TRACKING &&
        props.match.path !== addLocaleToUrl('/users') &&
        props.match.path !== addLocaleToUrl('/users/:userId')
      ) {
        return <Redirect to={addLocaleToUrl('/users')} />;
      }

      return isPasswordChangeRequired ? (
        <Route
          {...rest}
          path={addLocaleToUrl('/update-password')}
          component={UpdatePassword}
        />
      ) : (
        <Component {...props} locale={locale} />
      );
    }
    return <Redirect to={addLocaleToUrl('/login')} />;
  };
  return <Route {...rest} render={props => getRoute(props)} />;
};

PrivateRoute.propTypes = {
  match: PropTypes.shape({ path: PropTypes.string }).isRequired,
  locale: PropTypes.string.isRequired,
  component: PropTypes.oneOfType([PropTypes.node, PropTypes.func]).isRequired
};

export default PrivateRoute;
